// Finance Settings Storage — Task 23
//
// Company-wide finance settings backed by the server-side API.
//
//   GET  /api/settings/finance  → load on mount / bootstrap
//   PUT  /api/settings/finance  → save on user action
//
// A module-level cache allows synchronous getFinanceSettings() calls
// (used inside column accessors, data-service transforms, etc.) to
// return the latest known value without awaiting a network round-trip.
// The cache is populated by fetchFinanceSettings() which should be
// called once during app initialisation and again whenever the setting
// is saved.

import type { GpCalculationMethod } from './gp-calculation';
import { apiFetch } from './api-error-handler';
import { getApiBaseUrl } from './data-service';

/**
 * Revenue Display Mode — controls which revenue rows appear in the Project P&L Report.
 *
 * 'invoiced'    — Show Invoiced Revenue only (actualSales — what has been billed)
 * 'recognised'  — Show Recognised Revenue only (from Revenue Recognition schedule — Phase 2)
 * 'both'        — Show both Invoiced and Recognised Revenue rows (default)
 *
 * For T&M businesses that simply bill time, 'invoiced' keeps the P&L clean.
 * For fixed-price businesses with formal revenue recognition, 'both' is recommended.
 */
export type RevenueDisplayMode = 'invoiced' | 'recognised' | 'both';

export interface FinanceSettings {
  /**
   * GP Calculation Method — determines how Gross Profit and Gross Margin %
   * are calculated across all Financials tab cards, charts, and tables.
   *
   * 'simple'       — Revenue minus all direct costs (default)
   * 'contribution' — Revenue minus Labour and Subcontractors only
   * 'fully-loaded' — Revenue minus direct costs plus overhead absorption
   */
  gpCalculationMethod: GpCalculationMethod;

  /**
   * Revenue Display Mode — controls which revenue rows appear in the Project P&L Report.
   *
   * 'invoiced'    — Show Invoiced Revenue only
   * 'recognised'  — Show Recognised Revenue only (Phase 2)
   * 'both'        — Show both Invoiced and Recognised Revenue rows (default)
   */
  revenueDisplayMode: RevenueDisplayMode;

  /**
   * Portfolio Target Margin % — the firm's target gross margin percentage.
   * Used to render the dashed target line on the Portfolio Profitability chart
   * (PRD §3 Insight 3, task F10 / F18). When null, the target line is not rendered.
   */
  portfolioTargetMarginPercent?: number | null;

  /**
   * Target Utilisation % — the firm's target billable utilisation percentage.
   * Renders a dashed target line on the Utilisation Snapshot gauge
   * (PRD §3 Insight 5, task F12 / F18). When null, the target line is not rendered.
   */
  targetUtilisationPercent?: number | null;

  /**
   * Client Concentration Threshold % — the firm's maximum acceptable single-client
   * share of remaining contract value before a concentration warning is shown.
   * Renders the vertical threshold line on the Client Concentration Risk chart
   * (PRD §3 Insight 7, task F14 / F18). Defaults to 30 when not configured.
   */
  concentrationThresholdPercent?: number | null;

  /** Last updated ISO timestamp */
  lastUpdated?: string;
}

export const DEFAULT_FINANCE_SETTINGS: FinanceSettings = {
  gpCalculationMethod: 'simple',
  revenueDisplayMode: 'both',
};

// ── Module-level cache ──────────────────────────────────────────────────────
let _cache: FinanceSettings = { ...DEFAULT_FINANCE_SETTINGS };
let _hasFetched = false;

/**
 * Returns true if the cache has been populated by a completed API fetch.
 * Used by useFinanceSettings to skip the loading state when BootstrapGate
 * has already pre-fetched the data before the hook mounts.
 */
export function isFinanceSettingsCacheReady(): boolean {
  return _hasFetched;
}

/**
 * Synchronous read — returns the cached finance settings.
 * Always returns a valid object (defaults if the API hasn't been called yet).
 */
export function getFinanceSettings(): FinanceSettings {
  return _cache;
}

/**
 * Fetch finance settings from the server and update the cache.
 * Should be called during app bootstrap and after saving.
 * Returns the fetched settings.
 */
export async function fetchFinanceSettings(): Promise<FinanceSettings> {
  try {
    const baseUrl = getApiBaseUrl();
    const data = await apiFetch<{
      gpCalculationMethod: string;
      revenueDisplayMode?: string;
      portfolioTargetMarginPercent?: number | null;
      targetUtilisationPercent?: number | null;
      concentrationThresholdPercent?: number | null;
      lastUpdated?: string;
    }>(
      `${baseUrl}/settings/finance`
    );
    const method = (data.gpCalculationMethod || 'simple') as GpCalculationMethod;
    const revenueMode = (data.revenueDisplayMode || 'both') as RevenueDisplayMode;
    _cache = {
      gpCalculationMethod: method,
      revenueDisplayMode: revenueMode,
      portfolioTargetMarginPercent: data.portfolioTargetMarginPercent ?? null,
      targetUtilisationPercent: data.targetUtilisationPercent ?? null,
      concentrationThresholdPercent: data.concentrationThresholdPercent ?? null,
      lastUpdated: data.lastUpdated,
    };
    _hasFetched = true;
  } catch (error) {
    console.error('Error fetching finance settings from API:', error);
    if (!_hasFetched) {
      _cache = { ...DEFAULT_FINANCE_SETTINGS };
    }
  }
  return _cache;
}

/**
 * Save finance settings to the server and update the cache.
 */
export async function saveFinanceSettings(settings: FinanceSettings): Promise<void> {
  const baseUrl = getApiBaseUrl();
  const data = await apiFetch<{
    gpCalculationMethod: string;
    revenueDisplayMode?: string;
    portfolioTargetMarginPercent?: number | null;
    targetUtilisationPercent?: number | null;
    concentrationThresholdPercent?: number | null;
    lastUpdated?: string;
  }>(
    `${baseUrl}/settings/finance`,
    {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        gpCalculationMethod: settings.gpCalculationMethod,
        revenueDisplayMode: settings.revenueDisplayMode,
        portfolioTargetMarginPercent: settings.portfolioTargetMarginPercent ?? null,
        targetUtilisationPercent: settings.targetUtilisationPercent ?? null,
        concentrationThresholdPercent: settings.concentrationThresholdPercent ?? null,
      }),
    }
  );
  const method = (data.gpCalculationMethod || settings.gpCalculationMethod) as GpCalculationMethod;
  const revenueMode = (data.revenueDisplayMode || settings.revenueDisplayMode) as RevenueDisplayMode;
  _cache = {
    gpCalculationMethod: method,
    revenueDisplayMode: revenueMode,
    portfolioTargetMarginPercent: data.portfolioTargetMarginPercent ?? settings.portfolioTargetMarginPercent ?? null,
    targetUtilisationPercent: data.targetUtilisationPercent ?? settings.targetUtilisationPercent ?? null,
    concentrationThresholdPercent: data.concentrationThresholdPercent ?? settings.concentrationThresholdPercent ?? null,
    lastUpdated: data.lastUpdated,
  };
  _hasFetched = true;
}
